import axios from 'axios';
import moment from 'moment';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Box, Typography, IconButton, Menu, MenuItem, Chip, Tooltip, Dialog, DialogContent } from '@material-ui/core';
import { GetApp, Delete, Visibility, MoreVert, Close, InsertDriveFile, Image, PictureAsPdf, VideoLibrary, Audiotrack, Description, TableChart, Code } from '@material-ui/icons';
import { removeAttachment } from '../../store/Activities';

const useStyles = makeStyles((theme) => ({
  attachmentList: { padding: theme.spacing(0.5), display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) },
  attachmentItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: 160,
    padding: theme.spacing(0.75),
    borderRadius: 6,
    backgroundColor: '#F8F9FF',
    border: `1px solid #D7DCFF`,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      backgroundColor: '#E5EAFE70',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.01)'
    },
    '&:hover $attachmentActionsBtn': { opacity: 1 }
  },
  attachmentItemRow: { display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 },
  attachmentActionsBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 1,
    padding: 4,
    opacity: 0,
    transition: 'opacity 0.15s ease',
    backgroundColor: 'rgba(255,255,255,0.92)',
    boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
    '&:hover': { backgroundColor: '#fff' }
  },
  attachmentPreviewBox: {
    width: '100%',
    height: 96,
    borderRadius: 4,
    backgroundColor: '#fff',
    border: '1px solid #E8ECF2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    marginBottom: theme.spacing(0.5),
    // Reason: contain (not cover) so the full image is visible without opening the preview
    '& img': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' },
    '& $fileIconWrapper': { margin: 0, width: 44, height: 44 },
    '& $fileIcon': { fontSize: 28 }
  },
  fileIconWrapper: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  fileIcon: { fontSize: 20 },
  imageIcon: { color: '#e91e63', backgroundColor: '#fce4ec' },
  pdfIcon: { color: '#f44336', backgroundColor: '#ffebee' },
  videoIcon: { color: '#9c27b0', backgroundColor: '#f3e5f5' },
  audioIcon: { color: '#ff9800', backgroundColor: '#fff3e0' },
  documentIcon: { color: '#2196f3', backgroundColor: '#e3f2fd' },
  spreadsheetIcon: { color: '#4caf50', backgroundColor: '#e8f5e9' },
  codeIcon: { color: '#00bcd4', backgroundColor: '#e0f7fa' },
  defaultIcon: { color: '#607d8b', backgroundColor: '#eceff1' },
  fileInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '& .detail-area': { lineHeight: 1 },
    '& .detail-area .MuiTypography-root': { fontSize: '0.5625rem', lineHeight: 1.2 }
  },
  fileName: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    lineHeight: 1.2,
    // Reason: bootstrap gives h5 an 8px margin-bottom — the main source of footer height
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#333'
  },
  fileMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: 2,
    fontSize: '0.6875rem',
    color: '#666'
  },
  fileSize: {
    fontSize: '0.6875rem',
    height: 16,
    '& .MuiChip-label': { padding: '0 6px' }
  },
  previewDialog: { '& .MuiDialog-paper': { position: 'relative', maxWidth: '90vw', maxHeight: '90vh', backgroundColor: 'transparent', boxShadow: 'none' } },
  previewCloseBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 2,
    backgroundColor: 'rgba(255,255,255,0.9)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    '&:hover': { backgroundColor: '#fff' }
  },
  previewImage: { maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' },
  noAttachments: { textAlign: 'center', padding: theme.spacing(4), color: theme.palette.text.secondary }
}));

const getFileIcon = (mimeType, classes) => {
  let Icon = InsertDriveFile;
  let className = classes.defaultIcon;

  if (!mimeType) return { Icon, className };

  if (mimeType.startsWith('image/')) {
    Icon = Image;
    className = classes.imageIcon;
  } else if (mimeType === 'application/pdf') {
    Icon = PictureAsPdf;
    className = classes.pdfIcon;
  } else if (mimeType.startsWith('video/')) {
    Icon = VideoLibrary;
    className = classes.videoIcon;
  } else if (mimeType.startsWith('audio/')) {
    Icon = Audiotrack;
    className = classes.audioIcon;
  } else if (
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType.startsWith('text/')
  ) {
    Icon = Description;
    className = classes.documentIcon;
  } else if (
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'text/csv'
  ) {
    Icon = TableChart;
    className = classes.spreadsheetIcon;
  } else if (
    mimeType === 'application/javascript' ||
    mimeType === 'application/json' ||
    mimeType === 'application/xml' ||
    mimeType.includes('script') ||
    mimeType.includes('code')
  ) {
    Icon = Code;
    className = classes.codeIcon;
  }

  return { Icon, className };
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0 || isNaN(bytes)) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AttachmentList = ({ attachments = [], onDelete, showUploadedBy = true, enableActions = true }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const deletingRef = React.useRef(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [previewErrors, setPreviewErrors] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalDroppedAlert, setModalDroppedAlert] = useState(false);
  const [modalViewFileError, setModalViewFileError] = useState(false);
  const [modalDeleteFileError, setModalDeleteFileError] = useState(false);

  const handleMenuOpen = (event, attachment) => {
    setAnchorEl(event.currentTarget);
    setSelectedAttachment(attachment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedAttachment - keep it for delete modal
  };

  const handleDownload = async () => {
    if (!selectedAttachment) return;

    const attachment = selectedAttachment;
    handleMenuClose();

    try {
      const response = await axios.get(`/attachments/${attachment['_id']}/signed-url`, { params: { download: 'true' } });
      const signedUrl = response['data']['body']?.['url'] || response['data']['url'];
      if (!signedUrl) {
        setModalDroppedAlert(true);
        return;
      }

      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = attachment['originalName'] || attachment['fileName'] || attachment['name'] || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      setModalDroppedAlert(true);
    }
  };

  const handleView = async (attachment) => {
    try {
      const response = await axios.get(`/attachments/${attachment['_id']}/signed-url`);
      const signedUrl = response['data']['body']['url'];

      setSelectedAttachment(attachment);
      setPreviewUrl(signedUrl);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error viewing file:', error);
      setModalViewFileError(true);
    }
    handleMenuClose();
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl('');
  };

  const handleDelete = () => {
    if (!selectedAttachment || deletingRef.current) return;

    deletingRef.current = true;

    // Close modal immediately
    setDeleteModalOpen(false);
    // Dispatch action - the saga will handle the API call
    dispatch(removeAttachment(selectedAttachment['_id']));
    // Call parent callback
    if (onDelete) onDelete(selectedAttachment['_id']);
    // Clear selection
    setSelectedAttachment(null);
    // Reset flag
    setTimeout(() => { deletingRef.current = false; }, 500);
  };


  // Reason: ref (not state) marks ids as requested synchronously, so re-renders can't fire duplicate fetches
  const requestedThumbnailsRef = React.useRef(new Set());

  React.useEffect(() => {
    const imageIds = attachments
      .filter((attachment) => attachment['mimeType']?.startsWith('image/'))
      .map((attachment) => attachment['_id'])
      .filter((id) => id && !requestedThumbnailsRef.current.has(id));
    if (imageIds.length === 0) return;

    imageIds.forEach((id) => requestedThumbnailsRef.current.add(id));
    axios.post('/attachments/signed-urls', { ids: imageIds })
      .then((response) => {
        const urls = response['data']['body']?.['urls'] || {};
        setThumbnailUrls((prev) => ({ ...prev, ...urls }));
      })
      .catch((error) => console.error('Error loading attachment previews:', error));
  }, [attachments]);
  if (attachments.length === 0) {
    return (
      <Box className={classes.noAttachments}>
        <InsertDriveFile style={{ fontSize: 48, marginBottom: 8 }} />
        <Typography variant="body2">No attachments yet</Typography>
      </Box>
    );
  }

  // console.log('**************attachments', attachments);
  return (
    <div className='attchment-sidebar'>
      <Box className={classes.attachmentList}>
        {attachments.map((attachment) => {
          const { Icon, className } = getFileIcon(attachment['mimeType'] || attachment['type'], classes);
          const isImage = (attachment['mimeType'] || attachment['type'] || '').startsWith('image/');
          const previewSrc = isImage && !previewErrors[attachment['_id']] ? thumbnailUrls[attachment['_id']] : null;
          return (
            <Box
              key={attachment['_id']}
              className={classes.attachmentItem}
              onClick={() => handleView(attachment)}
            >
              {enableActions && (
                <IconButton
                  size="small"
                  className={classes.attachmentActionsBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, attachment);
                  }}
                >
                  <MoreVert style={{ fontSize: 18 }} />
                </IconButton>
              )}
              {/* Reason: plain divs — legacy style.css pads every nested .MuiBox-root in the deal accordion */}
              <div className={classes.attachmentPreviewBox}>
                {previewSrc ? (
                  <img
                    src={previewSrc}
                    alt={attachment['originalName'] || attachment['fileName'] || 'Preview'}
                    onError={() => setPreviewErrors((prev) => ({ ...prev, [attachment['_id']]: true }))}
                  />
                ) : (
                  <div className={`${classes.fileIconWrapper} ${className}`}>
                    <Icon className={classes.fileIcon} />
                  </div>
                )}
              </div>
              <div className={classes.attachmentItemRow}>
                <Box className={classes.fileInfo}>
                  <div className='title-area'>
                    <h5 className={classes.fileName}> {attachment['originalName'] || attachment['fileName'] || attachment['name'] || 'Unnamed file'}</h5>

                  </div>
                  <div className='detail-area'>
                    {!showUploadedBy && (
                      <Typography variant="caption">
                        {moment(attachment['createdAt']).format('MMM D, YYYY')}
                      </Typography>
                    )}
                    {showUploadedBy && attachment['uploadedBy'] && (
                      <Typography variant="caption">
                        {attachment['uploadedBy']['firstName']} • {moment(attachment['createdAt']).format('MMM D')} • {formatFileSize(attachment['fileSize'] || attachment['size'] || 0)}
                      </Typography>
                    )}
                  </div>
                </Box>
              </div>
            </Box>
          );
        })}

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} classes={{ paper: "attchement-dropdpwon" }}>
          <MenuItem onClick={() => handleView(selectedAttachment)}>
            <Visibility fontSize="small" style={{ marginRight: 8 }} />
            View
          </MenuItem>
          <MenuItem onClick={handleDownload}>
            <GetApp fontSize="small" style={{ marginRight: 8 }} />
            Download
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); setDeleteModalOpen(true); }}>
            <Delete fontSize="small" style={{ marginRight: 8 }} />
            Delete
          </MenuItem>
        </Menu>

        <Dialog open={previewOpen} onClose={closePreview} className={classes.previewDialog} maxWidth={false} fullWidth>
          <IconButton className={classes.previewCloseBtn} size="small" onClick={closePreview} aria-label="Close preview">
            <Close />
          </IconButton>
          <DialogContent style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16, backgroundColor: 'transparent' }}>
            {selectedAttachment?.['mimeType']?.startsWith('image/') && (
              <img src={previewUrl} alt="Preview" className={classes.previewImage} />
            )}
            {selectedAttachment?.['mimeType'] === 'application/pdf' && (
              <iframe src={previewUrl} title="PDF Preview" style={{ width: '100%', height: '80vh', border: 'none' }} />
            )}
            {selectedAttachment?.['mimeType']?.startsWith('video/') && (
              <video src={previewUrl} controls style={{ maxWidth: '100%', maxHeight: '80vh' }} />
            )}
            {selectedAttachment?.['mimeType']?.startsWith('audio/') && (
              <audio src={previewUrl} controls style={{ width: '100%' }} />
            )}
            {selectedAttachment && !selectedAttachment['mimeType']?.startsWith('image/') && selectedAttachment['mimeType'] !== 'application/pdf' && !selectedAttachment['mimeType']?.startsWith('video/') && !selectedAttachment['mimeType']?.startsWith('audio/') && (
              <Box style={{ textAlign: 'center', padding: 32 }}>
                <InsertDriveFile style={{ fontSize: 64, color: '#999', marginBottom: 16 }} />
                <Typography variant="body1" style={{ marginBottom: 16 }}>
                  {selectedAttachment['originalName'] || selectedAttachment['fileName'] || 'File'}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                  Preview not available for this file type
                </Typography>
                <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                  Open in new tab
                </a>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Modal isOpen={deleteModalOpen} toggle={() => {
          setDeleteModalOpen(false); setSelectedAttachment(null);
        }} className={`main-modal new-user-modal modal-dialog-centered endcall-modal`}>
          <ModalHeader toggle={() => {
            setDeleteModalOpen(false);
            setSelectedAttachment(null);
          }}>
            <h3>Confirm delete</h3>
          </ModalHeader>
          <ModalBody>
            <div className='add-content mt-4'>
              <div className='group-from'>
                <p>Are you sure you want to delete <strong>{selectedAttachment?.['originalName'] || selectedAttachment?.['fileName'] || 'this file'}</strong>?</p>
              </div>

              <div className='group-from btn-group mt-4'>
                <button
                  type="button"
                  className='btn-style-new'
                  onClick={(e) => {
                    console.log('[DELETE BUTTON] Clicked');
                    e.preventDefault();
                    e.stopPropagation();
                    handleDelete();
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className='btn-style-border'
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setSelectedAttachment(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        {/* --------------- Altert Dropped--------------- */}
        <Modal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
          <ModalHeader toggle={() => setModalDroppedAlert(false)}></ModalHeader>
          <ModalBody>
            <div className='add-content'>
              <h2>Failed to download file. Please try again.</h2>
              <div className='templates-inner'>
                <div className='btn-group'>
                  <button className='btn-style-new' onClick={() => setModalDroppedAlert(false)}>Ok</button>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>

        {/* --------------- Altert Dropped--------------- */}
        <Modal isOpen={modalViewFileError} toggle={() => setModalViewFileError(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
          <ModalHeader toggle={() => setModalViewFileError(false)}></ModalHeader>
          <ModalBody>
            <div className='add-content'>
              <h2>Failed to view file. Please try again.</h2>
              <div className='templates-inner'>
                <div className='btn-group'>
                  <button className='btn-style-new' onClick={() => setModalViewFileError(false)}>Ok</button>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={modalDeleteFileError} toggle={() => setModalDeleteFileError(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
          <ModalHeader toggle={() => setModalDeleteFileError(false)}></ModalHeader>
          <ModalBody>
            <div className='add-content'>
              <h2>Failed to delete file. Please try again.</h2>
              <div className='templates-inner'>
                <div className='btn-group'>
                  <button className='btn-style-new' onClick={() => setModalDeleteFileError(false)}>Ok</button>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>

      </Box>
    </div>
  );
};

export default AttachmentList;